import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { supabase, ICAL_SYNC_ENABLED } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import ICalSettingsModal from '../components/ICalSettingsModal';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const PROPERTY_COLORS = {
  "It's Happy Hour": '#2d6a8a',
  '4400 Windswept': '#6a2d8a',
};

const ALL_PROPERTIES = 'All Properties';

export default function Calendar({ session, currentView, onNavigate }) {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState('All Properties');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchProperties();
    fetchBookings();
  }, []);

  // Auto-dismiss sync result toast
  useEffect(() => {
    if (syncResult) {
      const timer = setTimeout(() => setSyncResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [syncResult]);

  const fetchProperties = async () => {
    const { data } = await supabase.from('properties').select('*').order('name');
    if (data) setProperties(data);
  };

  const fetchBookings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('bookings')
      .select('*, properties(name)')
      .order('check_in');
    if (data) setBookings(data);
    if (error) console.error(error);
    setLoading(false);
  };

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);

    const { data, error } = await supabase.functions.invoke('sync-ical', {
      body: {},
    });

    if (error) {
      setSyncResult({ type: 'error', text: `Sync failed: ${error.message}` });
    } else if (data?.success) {
      const summary = (data.results || [])
        .map(r => r.error ? `${r.property_name}: ${r.error}` : `${r.property_name}: ${r.bookings_synced} synced`)
        .join(', ');
      setSyncResult({ type: 'success', text: summary || 'Sync complete' });
      await fetchBookings();
    } else {
      setSyncResult({ type: 'error', text: data?.error || 'Sync failed' });
    }

    setSyncing(false);
  }, []);

  // Filter bookings by property
  const filtered = useMemo(() => {
    if (selectedProperty === 'All Properties') return bookings;
    return bookings.filter(b => b.properties?.name === selectedProperty);
  }, [bookings, selectedProperty]);

  // Convert bookings to calendar events
  const events = useMemo(() => filtered.map(b => ({
    id: b.id,
    title: b.guest_name || 'Booking',
    start: new Date(b.check_in + 'T12:00:00'),
    end: new Date(b.check_out + 'T12:00:00'),
    allDay: true,
    resource: {
      propertyName: b.properties?.name,
      color: PROPERTY_COLORS[b.properties?.name] || '#7B3F6E',
    },
  })), [filtered]);

  // Stats
  const today = new Date().toISOString().split('T')[0];
  const upcoming = bookings.filter(b => b.check_in >= today).length;
  const nextCheckIn = bookings
    .filter(b => b.check_in >= today)
    .sort((a, b) => a.check_in.localeCompare(b.check_in))[0];

  // Booking counts for sidebar
  const countFor = (prop) => {
    const future = bookings.filter(b => b.check_in >= today);
    if (prop === 'All Properties') return future.length;
    return future.filter(b => b.properties?.name === prop).length;
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.resource.color,
      border: 'none',
      borderRadius: '4px',
      color: 'white',
      fontSize: '12px',
      padding: '2px 6px',
    },
  });

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Properties</div>
          <div
            className={`sidebar-item ${selectedProperty === ALL_PROPERTIES ? 'active' : ''}`}
            onClick={() => setSelectedProperty(ALL_PROPERTIES)}
          >
            All Properties <span className="count">{countFor(ALL_PROPERTIES)}</span>
          </div>
          {properties.map(p => (
            <div
              key={p.id}
              className="sidebar-item sidebar-property-link"
              onClick={() => onNavigate('property-hub', p.id)}
            >
              {p.name} <span className="sidebar-arrow">&rarr;</span>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{upcoming}</span>
            <span className="stat-label">Upcoming</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {nextCheckIn ? format(new Date(nextCheckIn.check_in + 'T12:00:00'), 'MMM d') : '—'}
            </span>
            <span className="stat-label">Next Check-in</span>
          </div>
          {ICAL_SYNC_ENABLED && (
            <>
              <button
                className="sync-btn"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
              <button
                className="settings-btn"
                onClick={() => setShowSettings(true)}
              >
                Settings
              </button>
            </>
          )}
        </div>

        {/* Sync result toast */}
        {syncResult && (
          <div className={`sync-result ${syncResult.type}`}>
            {syncResult.text}
          </div>
        )}

        {/* Calendar */}
        <div className="content-area calendar-content-area">
          <div className="calendar-legend">
            {Object.entries(PROPERTY_COLORS).map(([name, color]) => (
              <span key={name} className="legend-item">
                <span className="legend-dot" style={{ background: color }} />
                {name}
              </span>
            ))}
          </div>

          {loading ? (
            <div className="empty-state"><p>Loading calendar...</p></div>
          ) : (
            <div className="calendar-container">
              <BigCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                views={['month', 'week']}
                defaultView="month"
                eventPropGetter={eventStyleGetter}
                style={{ height: '100%' }}
                popup
              />
            </div>
          )}
        </div>
      </main>

      {/* Settings Modal */}
      {ICAL_SYNC_ENABLED && showSettings && (
        <ICalSettingsModal
          properties={properties}
          onClose={() => { setShowSettings(false); fetchProperties(); }}
          onSynced={fetchBookings}
        />
      )}
    </div>
  );
}
